import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow, format } from "date-fns";
import { ChevronDown, ChevronRight, AlertTriangle, Clock, Store } from "lucide-react";
import { usePageTitle } from "@/hooks/use-page-title";

type ActionEntry = {
  method: string;
  endpoint: string;
  timestamp: string;
  // Admin Access Layer audit hardening — optional on entries written before it.
  summary?: string;
  status?: number;
};

type LogEntry = {
  id: number;
  impersonatorId: number;
  targetUserId: number;
  startedAt: string;
  endedAt: string | null;
  actionsTaken: ActionEntry[];
  ipAddress: string | null;
  userAgent: string | null;
  impersonatorEmail: string;
  targetEmail: string;
};

type VendorLogEntry = {
  id: number;
  impersonatorId: number;
  targetVendorId: number;
  targetVendorTeamMemberId: number | null;
  startedAt: string;
  endedAt: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  impersonatorEmail?: string;
  targetVendorName?: string;
  targetTeamMemberName?: string | null;
};

type FailedAttempt = {
  id: number;
  attemptedBy: string;
  reason: string;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
};

function duration(startedAt: string, endedAt: string | null) {
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const ms = end - start;
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

function LogRow({ entry }: { entry: LogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const isActive = !entry.endedAt;

  return (
    <div className="border-b border-gray-100 last:border-0" data-testid={`row-log-${entry.id}`}>
      <button
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">{entry.impersonatorEmail}</span>
            <span className="text-xs text-gray-400">→</span>
            <span className="text-sm text-gray-700">{entry.targetEmail}</span>
            {isActive && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">Active</Badge>}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {format(new Date(entry.startedAt), "MMM d, yyyy HH:mm")} · {duration(entry.startedAt, entry.endedAt)} · {entry.actionsTaken.length} action{entry.actionsTaken.length !== 1 ? "s" : ""}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="px-11 pb-3 space-y-2">
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
            <div><span className="font-medium">IP:</span> {entry.ipAddress || "—"}</div>
            <div><span className="font-medium">Ended:</span> {entry.endedAt ? format(new Date(entry.endedAt), "HH:mm:ss") : "Still active"}</div>
            <div className="col-span-2 break-all"><span className="font-medium">UA:</span> {entry.userAgent || "—"}</div>
          </div>
          {entry.actionsTaken.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">Actions taken:</div>
              <ul className="space-y-0.5">
                {entry.actionsTaken.map((a, i) => (
                  <li key={i} className="text-xs text-gray-600 bg-gray-50 px-2 py-0.5 rounded flex gap-2 items-baseline">
                    <span className="font-mono font-semibold text-gray-500 shrink-0">{a.method}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block truncate">{a.summary || a.endpoint}</span>
                      {a.summary && <span className="block font-mono text-[10px] text-gray-400 truncate">{a.endpoint}</span>}
                    </span>
                    {typeof a.status === "number" && (
                      <span className={`font-mono shrink-0 ${a.status < 400 ? "text-emerald-600" : "text-red-600"}`}>{a.status}</span>
                    )}
                    <span className="text-gray-400 shrink-0">{format(new Date(a.timestamp), "HH:mm:ss")}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {entry.actionsTaken.length === 0 && (
            <div className="text-xs text-gray-400 italic">No write actions taken</div>
          )}
        </div>
      )}
    </div>
  );
}

function VendorLogRow({ entry }: { entry: VendorLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const isActive = !entry.endedAt;

  return (
    <div className="border-b border-gray-100 last:border-0" data-testid={`row-vendor-log-${entry.id}`}>
      <button
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}
        <Store className="h-3.5 w-3.5 text-gray-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">{entry.impersonatorEmail ?? `Admin #${entry.impersonatorId}`}</span>
            <span className="text-xs text-gray-400">→</span>
            <span className="text-sm text-gray-700">
              {entry.targetVendorName ?? `Vendor #${entry.targetVendorId}`}
              {entry.targetTeamMemberName && (
                <span className="text-gray-400"> · {entry.targetTeamMemberName}</span>
              )}
            </span>
            {isActive && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">Active</Badge>}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {format(new Date(entry.startedAt), "MMM d, yyyy HH:mm")} · {duration(entry.startedAt, entry.endedAt)}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="px-11 pb-3 space-y-2">
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
            <div><span className="font-medium">Vendor ID:</span> {entry.targetVendorId}</div>
            <div><span className="font-medium">Team member ID:</span> {entry.targetVendorTeamMemberId ?? "—"}</div>
            <div><span className="font-medium">IP:</span> {entry.ipAddress || "—"}</div>
            <div><span className="font-medium">Ended:</span> {entry.endedAt ? format(new Date(entry.endedAt), "HH:mm:ss") : "Still active"}</div>
            <div className="col-span-2 break-all"><span className="font-medium">UA:</span> {entry.userAgent || "—"}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppDevToolsLog() {
  usePageTitle("Dev: Log");
  const logQuery = useQuery<{ entries: LogEntry[] }>({
    queryKey: ["/api/app/impersonation/log"],
    queryFn: async () => {
      const res = await fetch("/api/app/impersonation/log", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    staleTime: 30_000,
  });

  const vendorLogQuery = useQuery<{ entries: VendorLogEntry[] }>({
    queryKey: ["/api/app/impersonation/vendor-log"],
    queryFn: async () => {
      const res = await fetch("/api/app/impersonation/vendor-log", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    staleTime: 30_000,
  });

  const failedQuery = useQuery<{ attempts: FailedAttempt[] }>({
    queryKey: ["/api/app/impersonation/failed-attempts"],
    queryFn: async () => {
      const res = await fetch("/api/app/impersonation/failed-attempts", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    staleTime: 30_000,
  });

  const entries = logQuery.data?.entries ?? [];
  const vendorEntries = vendorLogQuery.data?.entries ?? [];
  const attempts = failedQuery.data?.attempts ?? [];

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">
      <div className="mb-2">
        <h1 className="text-2xl font-semibold text-gray-900" data-testid="heading-impersonation-log">Impersonation Log</h1>
        <p className="text-sm text-gray-500 mt-1">Full audit trail of all impersonation sessions and failed access attempts.</p>
      </div>

      {/* User/host impersonation sessions */}
      <div>
        <h2 className="text-base font-medium text-gray-900 mb-3">User Sessions ({entries.length})</h2>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {logQuery.isLoading && <div className="py-8 text-center text-sm text-gray-400">Loading…</div>}
          {!logQuery.isLoading && entries.length === 0 && (
            <div className="py-8 text-center text-sm text-gray-400">No sessions yet</div>
          )}
          {entries.map((e) => <LogRow key={e.id} entry={e} />)}
        </div>
      </div>

      {/* Vendor impersonation sessions */}
      <div>
        <h2 className="text-base font-medium text-gray-900 mb-3 flex items-center gap-2">
          <Store className="h-4 w-4 text-gray-500" />
          Vendor Sessions ({vendorEntries.length})
        </h2>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {vendorLogQuery.isLoading && <div className="py-8 text-center text-sm text-gray-400">Loading…</div>}
          {!vendorLogQuery.isLoading && vendorEntries.length === 0 && (
            <div className="py-8 text-center text-sm text-gray-400">No vendor sessions yet</div>
          )}
          {vendorEntries.map((e) => <VendorLogRow key={e.id} entry={e} />)}
        </div>
      </div>

      {/* Failed attempts */}
      <div>
        <h2 className="text-base font-medium text-gray-900 mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          Failed Attempts ({attempts.length})
        </h2>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {failedQuery.isLoading && <div className="py-8 text-center text-sm text-gray-400">Loading…</div>}
          {!failedQuery.isLoading && attempts.length === 0 && (
            <div className="py-8 text-center text-sm text-gray-400">No failed attempts</div>
          )}
          {attempts.map((a) => (
            <div key={a.id} className="border-b border-gray-100 last:border-0 px-4 py-3" data-testid={`row-failed-${a.id}`}>
              <div className="flex items-center gap-3">
                <Badge variant="destructive" className="text-xs shrink-0">{a.reason}</Badge>
                <span className="text-sm text-gray-700">{a.attemptedBy}</span>
                <span className="text-xs text-gray-400 ml-auto flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDistanceToNow(new Date(a.createdAt), { addSuffix: true })}
                </span>
              </div>
              {a.ipAddress && <div className="text-xs text-gray-400 mt-0.5">IP: {a.ipAddress}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
